import { ChevronDown } from 'lucide-react';
import { FlagIcon } from './FlagIcon';
import {
  DEFAULT_DIAL_ISO,
  DIAL_CODES,
  countryFor,
  dialFor,
  exampleFor,
  nationalPhone,
} from './dialCodes';
import { useFormattedSelection } from '@/shared/hooks/useFormattedSelection';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

/**
 * ONE phone field, on every screen that asks for a number.
 *
 * It was written for MFA enrolment and stayed there, so the account-opening
 * wizard grew its own: a `ScrollSafeSelect` reading `+1` beside a separate
 * `Input`, two bordered controls with a gap between them. Same question, two
 * answers, and the wizard's was the weaker one — a bare dial code says nothing
 * about which country it belongs to, and `+1` is Canada AND the United States.
 *
 * The design decisions below are the enrolment field's, kept verbatim because
 * they were each paid for:
 *
 *   - THE CODE IS PICKED, NEVER TYPED. The commonest way a number fails is a
 *     user entering it the way they say it out loud, with no country code. A
 *     hint under the field is a rule to remember; this is a mistake made
 *     impossible.
 *   - ONE BORDER AROUND TWO CONTROLS, so the pair reads as one field, with a
 *     divider rather than a border on either half.
 *   - DROPDOWNMENU, NOT RADIX SELECT, and not a native `<select>`. A native
 *     `<option>` holds text only, which forces flag EMOJI — and an emoji's
 *     shape belongs to the system font, so Apple waves them and Windows drops
 *     them for two letters. Rectangles need real images, so the list must be
 *     one we render. Radix `Select.Content` then unconditionally mounts
 *     react-remove-scroll, which locks the page while the menu is open with no
 *     opt-out; `ScrollSafeSelect` exists in this repo for exactly that reason
 *     and follows the same approach. The trade is no OS picker on mobile.
 *   - THE DIAL CODE SITS INSIDE THE NUMBER FIELD, not editable. It reads as
 *     part of the number because it is part of the number, and it cannot be
 *     typed over, deleted, or entered twice.
 *
 * The value is an ISO country, not a dial code, because the dial code is not
 * unique and the country is what the user actually chose.
 */
export interface PhoneFieldProps {
  /** ISO 3166-1 alpha-2 of the selected country. */
  iso: string;
  onIso: (iso: string) => void;
  /** The LOCAL part only — the dial code is never in here. */
  value: string;
  onChange: (value: string) => void;
  /** `id` for the number input, so a caller's `<label htmlFor>` reaches it. */
  id?: string;
  /** Rendered inside the number field, right-aligned (e.g. a Verified pill). */
  badge?: React.ReactNode;
  /** Marks the control invalid for assistive tech and reddens the outline. */
  invalid?: boolean;
  /** Require a national number when an optional section is enabled. */
  required?: boolean;
  /**
   * Which surface this box is standing on. See the note at the container.
   *
   * `standalone` (the default) keeps the MFA enrolment screen exactly as it
   * was; `form` makes the box one of the account-opening form's fields.
   */
  variant?: 'standalone' | 'form';
}

export function PhoneField({
  iso,
  onIso,
  value,
  onChange,
  id,
  badge,
  invalid,
  required,
  variant = 'standalone',
}: PhoneFieldProps) {
  // NORMALISED ONCE, and the reason is not defensiveness for its own sake.
  //
  // `countryFor`, `dialFor` and `exampleFor` each fall back to the default on
  // their own, so an unknown code already drew Canada's name, Canada's dial
  // code and Canada's placeholder — while the FLAG, which has no fallback,
  // drew nothing and then crashed the page outright. Four independent answers
  // to one question is how they disagreed. Resolving the country here means
  // the field shows one country or the other, never a composite.
  //
  // It is reached in ordinary use, not only by a bad caller: the wizard's
  // country field is newer than the drafts it restores, so every session in
  // flight arrives with none.
  const active = DIAL_CODES.some((c) => c.iso === iso) ? iso : DEFAULT_DIAL_ISO;

  const displayed = nationalPhone(active, value);
  const { inputRef, captureSelection } = useFormattedSelection(displayed);

  return (
    /* TWO SURFACES, TWO BOXES, ONE CONTROL.
     *
     * `standalone` is what MFA enrolment asks for and what this was built as:
     * a taller box with a shadow and a dark focus border, sitting alone on a
     * screen that asks one question.
     *
     * `form` is the account-opening wizard, where this box stands in a grid
     * beside a dozen shadcn `Input`s. There it has to be one of them —
     * `h-9`, `bg-input-background`, and the ring the rest of the form focuses
     * with — or the row reads as two kinds of field and the eye stops on the
     * odd one. The difference was visible at rest and louder on focus: a thick
     * dark border where every neighbour draws a violet ring.
     *
     * A parameter rather than a rewrite, because the MFA screen's look is not
     * wrong there; it is simply not this form's.
     */
    <div
      className={
        variant === 'form'
          ? `relative h-9 w-full min-w-0 shrink-0 rounded-md border bg-input-background transition-[color,box-shadow] ${
              invalid
                ? 'border-destructive focus-within:border-destructive'
                : 'border-border focus-within:border-ring focus-within:ring-ring/50 focus-within:ring-1'
            }`
          : `relative rounded-md shrink-0 w-full border shadow-xs bg-transparent ${
              invalid
                ? 'border-destructive focus-within:border-destructive'
                : 'border-border focus-within:border-foreground'
            }`
      }
    >
      <div className="flex flex-row items-stretch overflow-clip rounded-[inherit] size-full">
        <DropdownMenu modal={false}>
          <DropdownMenuTrigger
            aria-label="Country"
            className="flex h-9 shrink-0 items-center gap-2 px-[12px] outline-none"
          >
            {/* Flag and country only. The dial code lives in the number field,
              where the thing it prefixes actually is. */}
            <FlagIcon iso={active} title={countryFor(active)} />
            <span className="text-base md:text-sm text-foreground">
              {countryFor(active)}
            </span>
            <ChevronDown className="size-4 shrink-0 opacity-50" />
          </DropdownMenuTrigger>

          <DropdownMenuContent
            align="start"
            className="max-h-[280px] overflow-y-auto"
          >
            {DIAL_CODES.map((c) => (
              <DropdownMenuItem
                key={c.iso}
                onSelect={() => onIso(c.iso)}
                className={`gap-2 ${c.iso === active ? 'bg-accent/50 font-medium' : ''}`}
              >
                <FlagIcon iso={c.iso} title={c.country} />
                <span>{c.country}</span>
                <span className="text-muted-foreground">+{c.dial}</span>
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
        {/* A divider, not a border on either control: the pair is one field and
          only the wrapper draws its outline. */}
        <span aria-hidden className="my-2 w-px bg-border" />
        <div className="box-border content-stretch flex gap-[8px] items-center px-[12px] py-[0px] relative w-full min-w-0">
          <span
            aria-hidden
            className="shrink-0 text-base md:text-sm text-foreground tabular-nums"
          >
            +{dialFor(active)}
          </span>
          {/* GROUPED ON SCREEN, BARE DIGITS IN THE STATE. The box showed
            `4383737501`, a run an advisor has to count to read back, while
            every other place this number appears — the folded holder row, a
            business card, the client saying it aloud — groups it. The dashes
            and spaces are display only: `onChange` strips them, so `value`,
            `isValidPhone` and the NBIN mapping see exactly what they always
            did. The grouping follows the COUNTRY picked beside it. */}
          <input
            id={id}
            required={required}
            ref={inputRef}
            value={displayed}
            onChange={(e) => {
              captureSelection(e.currentTarget);
              onChange(e.currentTarget.value.replace(/\D/g, ''));
            }}
            placeholder={exampleFor(active)}
            inputMode="tel"
            autoComplete="tel-national"
            aria-invalid={invalid || undefined}
            className="basis-0 flex flex-col grow justify-center leading-[0] min-h-px min-w-0 not-italic relative shrink-0 text-foreground h-9 text-base md:text-sm placeholder:text-muted-foreground bg-transparent border-none outline-none"
          />
          {badge && <div className="shrink-0">{badge}</div>}
        </div>
      </div>
    </div>
  );
}
